/* eslint
    new-cap: ["warn", { "capIsNew": false }]
*/

import PrescriptionModal, {
  ModalType
} from '@webModules/prescriptionModal';
import easyfyAxios from '@shared/easyfy.axios';
import { element, globalEvent, getCookie } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import { addCss, removeCss } from '@shared/easyfy.utils';
import easyfyToaster, { MessageSeverity } from '@shared/easyfy.toaster';

const ACTIVITY_THROTTLE_MS = 1000;

const throttle = (
  callback: (...args: unknown[]) => void,
  intervalMs: number | (() => number)
) => {
  let lastRun = 0;

  return (...args: unknown[]) => {
    const interval = typeof intervalMs === 'function' ? intervalMs() : intervalMs;
    const now = Date.now();
    if (now - lastRun >= interval) {
      lastRun = now;
      callback(...args);
    }
  };
};

export namespace Meds.Modules {
  @invoke
  export class ClientSession {
    static Instance: ClientSession;

    static reminderElement: HTMLElement;

    static reminderTime: number;

    static logoutTime: number;

    static reminderTimerHandler;

    static logoutTimerHandler;

    static activityEvents = ['click', 'keydown', 'scroll', 'touchstart'];

    static activityStorageKey = 'clientSessionActivity';

    private modal: PrescriptionModal;

    constructor() {
      ClientSession.Instance = this;
      this.modal = new PrescriptionModal();
    }

    onInit() {
      const logoutTimerCookie = getCookie('logoutTimer');
      if (logoutTimerCookie) {
        ClientSession.reminderElement = element('#logout-reminder');
        ClientSession.logoutTime = Number(logoutTimerCookie) * 1000;
        ClientSession.reminderTime = ClientSession.logoutTime - 60 * 1000;

        ClientSession.SetReminderTimer(ClientSession.reminderTime);
        ClientSession.SetLogoutTimer(ClientSession.logoutTime);

        ClientSession.RegisterActivityListeners();

        globalEvent({
          type: 'click',
          event: {
            action: 'keep-login',
            callback: ClientSession.KeepLogin
          }
        });
      } else {
        ClientSession.ShowLoggedOutModal();
      }

      ClientSession.CheckForFailedLogin();
    }

    static SetReminderTimer = (time) => {
      if (ClientSession.reminderTimerHandler) {
        clearTimeout(ClientSession.reminderTimerHandler);
      }

      ClientSession.reminderTimerHandler = setTimeout(() => {
        ClientSession.ActivateReminder();
      }, time);
    };

    static SetLogoutTimer = (time) => {
      if (ClientSession.logoutTimerHandler) {
        clearTimeout(ClientSession.logoutTimerHandler);
      }

      ClientSession.logoutTimerHandler = setTimeout(() => {
        ClientSession.ActivateLogout();
      }, time);
    };

    static ActivateReminder = async () => {
      const response = await easyfyAxios.get('/api/customersession?handler=issessionalive');
      if (response.data.loggedIn) {
        // show reminder modal
        removeCss(ClientSession.reminderElement, 'hidden');
        return;
      }

      // if not logged in, clear timeouts
      ClientSession.ClearLogoutTimers();
    };

    static ActivateLogout = () => {
      const formElement = ClientSession.reminderElement.querySelector('form');

      if (formElement) {
        formElement.submit();
      }
    };

    static KeepLogin = async () => {
      addCss(ClientSession.reminderElement, 'hidden');
      await easyfyAxios.get('/api/customersession?handler=keepsessionalive');
      ClientSession.ResetTimers();
    };

    static ResetTimers = () => {
      ClientSession.SetReminderTimer(ClientSession.reminderTime);
      ClientSession.SetLogoutTimer(ClientSession.logoutTime);
    };

    static ClearLogoutTimers = () => {
      clearTimeout(ClientSession.reminderTimerHandler);
      clearTimeout(ClientSession.logoutTimerHandler);
    };

    static RegisterActivityListeners = () => {
      ClientSession.activityEvents.forEach((eventName) => {
        document.addEventListener(eventName, ClientSession.OnUserActivity, {
          passive: true
        });
      });

      window.addEventListener('storage', ClientSession.OnActivityFromOtherTab);
    };

    static OnUserActivity = throttle(() => {
      // While the reminder modal is shown the explicit buttons govern the choice
      if (
        !ClientSession.reminderElement ||
        !ClientSession.reminderElement.classList.contains('hidden')
      ) {
        return;
      }

      ClientSession.ResetTimers();
      ClientSession.KeepServerSessionAlive();
      ClientSession.BroadcastActivity();
    }, ACTIVITY_THROTTLE_MS);

    static OnActivityFromOtherTab = (storageEvent) => {
      if (storageEvent.key !== ClientSession.activityStorageKey) {
        return;
      }

      // Another tab is active, so this tab's session is kept alive there
      if (ClientSession.reminderElement) {
        addCss(ClientSession.reminderElement, 'hidden');
      }
      ClientSession.ResetTimers();
    };

    // Ping at most twice per session window so the server's sliding session
    // never lapses while the user is active without a full page navigation.
    static KeepServerSessionAlive = throttle(() => {
      easyfyAxios.get('/api/customersession?handler=keepsessionalive');
    }, () => ClientSession.logoutTime / 2);

    static BroadcastActivity = () => {
      try {
        localStorage.setItem(ClientSession.activityStorageKey, Date.now().toString());
      } catch {
        // localStorage may be unavailable (private mode); cross-tab sync is best-effort
      }
    };

    static ShowLoggedOutModal = () => {
      const url = new URL(window.location.href);

      if (url.searchParams.has('logout')) {
        ClientSession.GetLogoutModal();
        url.searchParams.delete('logout');
        window.history.replaceState({}, '', url.toString());
      }
    };

    static GetLogoutModal = () => {
      const addToElement = element('body') as HTMLElement;

      if (addToElement) {
        ClientSession.Instance.modal.AppendModal(
          ModalType.LoggedOut,
          addToElement
        );
      }
    };

    static CheckForFailedLogin = () => {
      const url = new URL(window.location.href);
      const loginfailed = url.searchParams.get('loginfailed');
      const msg = url.searchParams.get('msg');

      if (!loginfailed) {
        return;
      }

      window.history.replaceState(
        null,
        '',
        url.href.replace(/(\?.*)|(#.*)/g, '')
      );

      const message = atob(msg);
      easyfyToaster.toaster(MessageSeverity.error, message, null, 10000);
    };
  }
}

export default Meds.Modules.ClientSession;
